<template>
  <!-- Header Top - Scroll Away -->
  <section
    v-show="!hidden && !hidePromoBarBasedOnRoute"
    v-style="{ cols: { sm: '1fr', lg: 'auto 1fr auto' }, h: { sm: '2.75rem', lg: '2.25rem' } }"
  >
    <div class="relative h-full bg-brand-1 c-white">
      <!-- Promo Bar -->
      <lazy-cms-section data-scroll-el="promo-bar" :section="sections.promoBar" />
    </div>
  </section>

  <!-- Header Main - Sticky -->
  <div
    v-show="!hidden"
    :aria-hidden="hideMainHeader"
    class="sticky top-0 z-above-header duration-300"
    :class="{ '-translate-y-full': hideMainHeader }"
    data-scroll-el="header"
    :inert="hideMainHeader"
  >
    <header
      ref="headerRef"
      class="b-b b-transparent duration"
      :class="[
        {
          'inset-x-0 bottom-a top-0 absolute': transparent,
          'bg-op-0': transparent && !isScrolledDown && !menuOpened,
          'b-grey-80': isScrolledDown && !transparent,
        },
        isDark ? 'bg-brand-1' : 'bg-brand-3',
      ]"
    >
      <div class="w-full flex items-center px-4 py-2 ease-out lg:grid lg:cols-[1fr_auto_1fr] lg:px-8 <lg:between">
        <!-- Logo - Mobile -->
        <base-link class="lg:hidden" to="/">
          <span class="sr-only">{{ $t.brand }} {{ $t.home }}</span>
          <vf-icon
            :class="{
              'c-brand-3': isDark,
              'c-brand-1': !isDark,
            }"
            height="38"
            :src="brandLogo"
            width="66"
          />
        </base-link>

        <!-- Logo - Desktop -->
        <div class="w-fit <lg:hidden">
          <base-link class="py-2" to="/">
            <span class="sr-only">{{ $t.brand }} {{ $t.home }}</span>
            <vf-icon
              class="transition-transform duration-500"
              :class="{
                'c-brand-1': !isDark,
                'c-brand-3': isDark,
              }"
              height="48"
              :src="brandLogo"
              width="88"
            />
          </base-link>
        </div>

        <lazy-cms-section v-if="megaMenu" :class="navItemHoverClasses" :section="sections.megaMenu" />

        <section class="ml-a flex center gap-2 lg:gap-3" data-test-id="menu-section">
          <transition name="fade">
            <base-button
              :aria-label="$t.searchIconLabel"
              class="h-8 w-8 flex center"
              :class="navItemHoverClasses"
              @click="openSearchPanel"
            >
              <vf-icon name="search" :size="{ sm: 'md', lg: 'lg' }" />
            </base-button>
          </transition>

          <!-- Account Button with Inline SignIn Dropdown -->
          <div ref="accountMenuRef" class="relative">
            <base-button
              aria-haspopup="dialog"
              class="h-8 w-8 flex center"
              data-test-id="account-button"
              @click="openAccountPanel"
            >
              <div v-if="!auth.loggedIn" :class="navItemHoverClasses">
                <span class="sr-only">{{ $t.myAccount }}</span>
                <vf-icon
                  class="pointer-events-none"
                  name="account"
                  :size="{ sm: 'md', lg: 'lg' }"
                />
              </div>
              <span v-else class="h-8 w-8 flex center rounded-full bg-brand-6 p-2 fw-bold <lg:text-sm">
                {{ userInitials }}
              </span>
            </base-button>

            <dialog-account-menu-parent v-slot="props">
              <dialog-account-menu v-bind="props" />
            </dialog-account-menu-parent>
            <account-dropdown v-model="isDropdownOpen" />
          </div>

          <!-- Cart -->
          <base-link
            class="group relative h-8 w-8 flex center"
            to="/cart"
            @click="$gtm.push('topNav.onClickUtilityLink', 'Cart')"
          >
            <span :class="navItemHoverClasses">
              <vf-icon name="bag" :size="{ sm: 'md', lg: 'lg' }" />
              <span class="sr-only">
                {{ replaceAll($pluralize($t.cartIconLabel, cart?.totalItems), { count: cart.totalItems }) }}
              </span>
              <client-only>
                <span
                  v-if="cart?.totalItems"
                  aria-hidden="true"
                  class="absolute right-0 top-0 m-0 h-3 w-3 flex select-none center rounded-full bg-brand-2 text-micro c-brand-3 lh-none ring ring-white lg:h-4 lg:w-4 lg:text-xs"
                >
                  {{ cart.totalItems }}
                </span>
              </client-only>
            </span>
          </base-link>

          <!-- Menu Button - Mobile -->
          <base-button
            :aria-label="$t.menuIconLabel"
            class="h-8 w-8 flex center lg:hidden"
            :class="navItemHoverClasses"
            data-test-id="vf-header-menu-button"
            @click="openMegaMenu"
          >
            <vf-icon name="menu" size="md" />
          </base-button>
        </section>
      </div>
    </header>
  </div>

  <!-- SEO: Hidden Mega Menu for Search Engines -->
  <base-lazy-hydrate when="never">
    <vf-mega-menu aria-hidden="true" class="o:hidden" :items="megaMenu" :links />
  </base-lazy-hydrate>

  <panel-mega-menu position="right" />
  <panel-search-hub
    v-if="!isChatOpen"
    class-content="!lg:w-190"
    style-backdrop="--un-bg-opacity: 0.25"
    variant="light"
  />
</template>

<script lang="ts" setup>
import { HeaderContextKey } from '#commerce/components/vf/header/context'

const { transparentMode = undefined, colorMode = undefined } = defineProps<{
  transparentMode?: boolean
  colorMode?: 'light' | 'dark'
  hidden?: boolean
}>()

const { isSomethingElseSticky, links, megaMenu, menuOpened, sections } = inject(HeaderContextKey)!

const auth = useAuthStore()
const cart = useCartStore()
const header = useHeaderStore()
const { $gtm, $feature, $t, $viewport } = useNuxtApp()
const { y } = useScroll(document)
const user = useUserStore()
const { brandLogo } = useAppConfig().assets
const route = useRoute()

const accountMenuRef = useTemplateRef<HTMLElement>('accountMenuRef')
const headerRef = toRef(header.refs, 'header')
const isDropdownOpen = ref(false)
const isChatOpen = ref(false)

const DialogAccountMenuParent = createDialog('account-menu', {
  type: 'panel',
  position: 'bottom'
})

const PanelMegaMenu = createPanel('mega-menu', { position: 'right' })
const PanelSearchHub = createPanel('ask-me-anything-search', { size: 'full' })

const mode = computed(() => colorMode ?? header.mode)
const transparent = computed(() => transparentMode ?? header.transparent)
const isDark = computed(() => mode.value === 'dark')
const isScrolledDown = computed(() => y.value > 25)
const hideMainHeader = computed(() => isSomethingElseSticky.value && isScrolledDown.value && !header.pageScrolledUp)
const navItemHoverClasses = computed(() => (
  {
    'c-brand-1': !isDark.value,
    'c-brand-3': isDark.value,
  }
))
const userInitials = computed(() =>
  `${user.details?.firstName?.slice(0, 1).toUpperCase() || ''}${user.details?.lastName?.slice(0, 1).toUpperCase() || ''}`
  || user.details?.email?.charAt(0).toUpperCase()
  || ''
)

const hidePromoBarBasedOnRoute = computed(() =>
  $feature.disablePromoBarOnRoutes.some((path) => convertPatternToRegex(path).test(route.path)))

const openAccountPanel = () => {
  $gtm.push('topNav.onClickUtilityLink', auth.loggedIn ? 'My account' : 'Sign In')
  if ($viewport.lg) isDropdownOpen.value = !isDropdownOpen.value
  else DialogAccountMenuParent.open({ classWrapper: 'o:pt-0 o:px-4 o:pb-4' })
}

const openMegaMenu = () => {
  $gtm.push('topNav.onClickHamburgerMenu', true)
  PanelMegaMenu.open(
    { items: megaMenu!, links: links.value },
    {
      classContent: 'o:w-full o:max-h-full',
      classWrapper: 'justify-end items-start <lg:ml-8 lg:m-8',
      styleBackdrop: 'background: rgba(0, 0, 0, 0.15)',
    },
  )
    .then(({ closedReason }: { closedReason?: string } = {}) => {
      if (closedReason !== 'navigation' && closedReason !== 'dialog')
        $gtm.push('topNav.onClickHamburgerMenu', false)
    })
}

const openSearchPanel = () => {
  $gtm.push('topNav.onClickUtilityLink', 'Search')
  PanelSearchHub.open()
}

const onClarityStateChange = ({ detail }: CustomEvent) => {
  isChatOpen.value = !!(detail.state && ['opened', 'maximized'].includes(detail.state))
}

onMounted(() => useEventListener(window, 'ClarityState:widgetState', onClarityStateChange))

const closeDropdown = () => {
  isDropdownOpen.value = false
}

watch([y, () => route.fullPath], closeDropdown)

onClickOutside(accountMenuRef, closeDropdown)
</script>
